import React, { useCallback, useRef, Suspense } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Stack,
  useColorModeValue,
  Container,
  Flex,
  Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { FaArrowRight, FaMicrophone, FaRegCalendarCheck } from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi2";
import { HeroAuxiliaryLinks } from "./HomeSignupCtas";
import { HOME_HERO } from "../../../content/productGuide";
import { gtagReportConversionThenNavigate } from "../../../utils/googleAdsPageViewConversion";

const HomeHeroR3FCanvas = React.lazy(() =>
  import("./HeroWebGLScene").then((mod) => ({ default: mod.HomeHeroR3FCanvas }))
);

const MotionBox = motion(Box);
const springConfig = { stiffness: 120, damping: 18, mass: 0.4 };

/**
 * Pointer-reactive tilt + THREE.js orb — keeps Manage360 recognizable on landing.
 */
function AssistantShowcase3D() {
  const ref = useRef(null);
  const pointer3dRef = useRef({ x: 0, y: 0 });
  const reduceMotion = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const xSpring = useSpring(x, springConfig);
  const ySpring = useSpring(y, springConfig);
  const rawRotateY = useTransform(xSpring, [-0.5, 0.5], [12, -12]);
  const rawRotateX = useTransform(ySpring, [-0.5, 0.5], [-8, 8]);

  const onMove = useCallback(
    (e) => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      const clx = Math.max(-0.5, Math.min(0.5, px));
      const cly = Math.max(-0.5, Math.min(0.5, py));
      pointer3dRef.current = { x: clx * 2.6, y: -cly * 2.6 };
      if (!reduceMotion) {
        x.set(clx);
        y.set(cly);
      }
    },
    [reduceMotion, x, y]
  );

  const onLeave = useCallback(() => {
    x.set(0);
    y.set(0);
    pointer3dRef.current = { x: 0, y: 0 };
  }, [x, y]);

  const labelBg = "whiteAlpha.200";
  const chipShadow = "0 12px 40px rgba(0,0,0,0.28)";

  return (
    <Box
      ref={ref}
      position="relative"
      w="100%"
      maxW={{ base: "100%", lg: "560px" }}
      h={{ base: "min(62vw, 300px)", sm: "340px", md: "440px" }}
      mx="auto"
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      style={{ perspective: "1400px" }}
      className="home-hero-3d-root"
    >
      <Box
        position="absolute"
        inset={0}
        borderRadius="2xl"
        overflow="hidden"
        bg="radial-gradient(ellipse 80% 60% at 50% 40%, rgba(15, 23, 42, 0.45) 0%, transparent 70%)"
        pointerEvents="none"
      />
      <MotionBox
        position="relative"
        w="100%"
        h="100%"
        minH={{ base: "360px", md: "440px" }}
        style={{
          rotateX: reduceMotion ? 0 : rawRotateX,
          rotateY: reduceMotion ? 0 : rawRotateY,
          transformStyle: "preserve-3d",
        }}
      >
        <Box
          position="absolute"
          inset={0}
          zIndex={0}
          overflow="hidden"
          borderRadius="2xl"
          bg="linear-gradient(160deg, #020617 0%, #134e4a 38%, #1e1b4b 100%)"
        >
          <Suspense
            fallback={
              <Box
                position="absolute"
                inset={0}
                bg="linear-gradient(160deg, #0f172a 0%, #115e59 40%, #1e1b4b 100%)"
              />
            }
          >
            <HomeHeroR3FCanvas pointerRef={pointer3dRef} reduceMotion={!!reduceMotion} />
          </Suspense>
        </Box>

        <Box
          position="absolute"
          bottom="10%"
          left="50%"
          zIndex={1}
          transform="translateX(-50%) translateZ(32px)"
          textAlign="center"
          pointerEvents="none"
        >
          <Box as={HiOutlineSparkles} w={7} h={7} color="teal.200" mx="auto" mb={1} />
          <Text fontSize="xs" fontWeight="bold" color="white" letterSpacing="wider" textTransform="uppercase">
            One dashboard
          </Text>
          <Text fontSize="2xs" color="whiteAlpha.700" lineHeight="short" mt={0.5}>
            3D preview · move pointer to explore
          </Text>
        </Box>

        <Box
          position="absolute"
          right={{ base: "2%", md: "4%" }}
          top="12%"
          zIndex={1}
          px={3}
          py={2}
          borderRadius="xl"
          bg={labelBg}
          backdropFilter="blur(12px)"
          boxShadow={chipShadow}
          style={{ transform: "translateZ(80px) rotateY(-5deg)" }}
          pointerEvents="none"
        >
          <Flex align="center" gap={2}>
            <Box as={FaMicrophone} color="cyan.200" />
            <Text fontSize="sm" color="white" fontWeight="semibold">
              Talk or tap
            </Text>
          </Flex>
        </Box>

        <Box
          position="absolute"
          left={{ base: "0%", md: "2%" }}
          bottom="20%"
          zIndex={1}
          px={3}
          py={2}
          borderRadius="xl"
          bg={labelBg}
          backdropFilter="blur(12px)"
          boxShadow={chipShadow}
          style={{ transform: "translateZ(96px) rotateY(4deg)" }}
          pointerEvents="none"
        >
          <Flex align="center" gap={2}>
            <Box as={FaRegCalendarCheck} color="teal.200" />
            <Text fontSize="sm" color="white" fontWeight="semibold">
              Calendar & reminders
            </Text>
          </Flex>
        </Box>

        <Box
          position="absolute"
          right="8%"
          bottom="6%"
          zIndex={1}
          px={2}
          py={1}
          borderRadius="md"
          bg="blackAlpha.500"
          borderWidth="1px"
          borderColor="whiteAlpha.200"
          style={{ transform: "translateZ(48px)" }}
          pointerEvents="none"
        >
          <Text fontSize="xs" color="teal.100">
            Free workspace to start
          </Text>
        </Box>
      </MotionBox>
    </Box>
  );
}

const Hero = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const goRegister = () => gtagReportConversionThenNavigate(navigate, "/register");
  const goLogin = () => gtagReportConversionThenNavigate(navigate, "/login");
  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  const textColor = useColorModeValue("white", "gray.100");

  return (
    <Box
      as="section"
      position="relative"
      overflow="hidden"
      color={textColor}
      pt={{ base: 14, md: 16, lg: 20 }}
      pb={{ base: 14, md: 20, lg: 24 }}
      px={{ base: 4, md: 8 }}
      className="home-hero-section"
    >
      <Box
        position="absolute"
        inset={0}
        zIndex={0}
        pointerEvents="none"
        bgGradient="linear(to-br, #0f172a 0%, #134e4a 38%, #1e293b 100%)"
      />
      <Box
        position="absolute"
        w="125%"
        h="65%"
        left="-12%"
        top="-18%"
        opacity={0.5}
        pointerEvents="none"
        filter="blur(60px)"
        bgGradient="radial(circle at 32% 36%, rgba(45, 212, 191, 0.42) 0%, transparent 54%)"
        zIndex={0}
      />
      <Box
        position="absolute"
        w="100%"
        h="72%"
        right="-22%"
        bottom="-18%"
        opacity={0.38}
        pointerEvents="none"
        filter="blur(72px)"
        bgGradient="radial(circle at 72% 58%, rgba(99, 102, 241, 0.5) 0%, transparent 55%)"
        zIndex={0}
      />
      <Box
        position="absolute"
        inset={0}
        bgGradient="linear(to-b, blackAlpha.200, transparent, blackAlpha.350)"
        pointerEvents="none"
        zIndex={0}
      />

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <Flex
          direction={{ base: "column", lg: "row" }}
          align="center"
          justify="space-between"
          gap={{ base: 10, lg: 12 }}
        >
          <Stack
            spacing={{ base: 6, md: 7 }}
            maxW={{ base: "100%", lg: "520px" }}
            textAlign={{ base: "center", lg: "left" }}
            align={{ base: "center", lg: "flex-start" }}
          >
            <Badge
              as={motion.div}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.48 }}
              colorScheme="teal"
              variant="subtle"
              borderRadius="full"
              textTransform="none"
              fontWeight="semibold"
              fontSize="xs"
              color="teal.100"
              bg="whiteAlpha.200"
              borderWidth="1px"
              borderColor="whiteAlpha.200"
              px={4}
              py={1.5}
              letterSpacing="0.03em"
            >
              <Stack direction="row" align="center" spacing={2} as="span" justify={{ base: "center", lg: "flex-start" }}>
                <HiOutlineSparkles />
                <Text as="span" fontSize="xs" fontWeight="bold">
                  {HOME_HERO.badge}
                </Text>
              </Stack>
            </Badge>

            <MotionBox
              as="p"
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 0.02 }}
              fontSize="xs"
              fontWeight="bold"
              letterSpacing="0.22em"
              textTransform="uppercase"
              color="teal.200"
              opacity={0.95}
              m={0}
            >
              Manage360
            </MotionBox>

            <Heading
              as={motion.h1}
              initial={reduceMotion ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.04 }}
              size={{ base: "2xl", md: "4xl" }}
              fontWeight="extrabold"
              letterSpacing="tight"
              lineHeight={{ base: 1.14, md: 1.08 }}
            >
              <Box as="span" display="block">
                {HOME_HERO.headlineLine1}
              </Box>
              <Box
                as="span"
                display="block"
                bgGradient="linear(to-r, teal.200, cyan.200, purple.200)"
                bgClip="text"
                color="transparent"
              >
                {HOME_HERO.headlineLine2}
              </Box>
            </Heading>

            <MotionBox
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 }}
              mt={{ base: 3, md: 4 }}
              maxW={{ base: "100%", sm: "34rem", lg: "36rem" }}
              mx={{ base: "auto", lg: 0 }}
              textAlign={{ base: "center", lg: "left" }}
            >
              <Stack spacing={{ base: 2, md: 2.5 }} align="stretch">
                <Text
                  color="teal.50"
                  fontSize={{ base: "md", sm: "lg", md: "xl" }}
                  fontWeight="medium"
                  lineHeight={{ base: 1.65, md: 1.58 }}
                  letterSpacing="0.01em"
                  mb={0}
                >
                  {HOME_HERO.line1}
                </Text>
                <Text
                  color="whiteAlpha.900"
                  fontSize={{ base: "sm", sm: "md", md: "lg" }}
                  fontWeight="normal"
                  lineHeight={{ base: 1.6, md: 1.55 }}
                  letterSpacing="0.015em"
                  opacity={0.92}
                  mb={0}
                >
                  {HOME_HERO.line2}
                </Text>
              </Stack>
            </MotionBox>

            <Stack
              as={motion.div}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 0.14 }}
              spacing={3}
              pt={1}
              w={{ base: "full", lg: "auto" }}
              align={{ base: "stretch", lg: "flex-start" }}
            >
              <Flex
                direction={{ base: "column", sm: "row" }}
                gap={3}
                w={{ base: "full", sm: "auto" }}
                justify={{ base: "stretch", sm: "flex-start" }}
                flexWrap="wrap"
              >
                <Button
                  as={motion.button}
                  whileHover={reduceMotion ? {} : { scale: 1.02 }}
                  whileTap={reduceMotion ? {} : { scale: 0.98 }}
                  colorScheme="teal"
                  bg="teal.400"
                  color="gray.900"
                  size="lg"
                  fontWeight="bold"
                  px={{ base: 6, md: 8 }}
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: "teal.300" }}
                  onClick={goRegister}
                  w={{ base: "full", sm: "auto" }}
                >
                  Try Manage360 free
                </Button>
                <Button
                  variant="outline"
                  borderColor="whiteAlpha.500"
                  color="white"
                  size="lg"
                  fontWeight="semibold"
                  px={{ base: 6, md: 8 }}
                  _hover={{ bg: "whiteAlpha.150" }}
                  onClick={goLogin}
                  w={{ base: "full", sm: "auto" }}
                >
                  Sign in
                </Button>
              </Flex>
              <Text fontSize="xs" color="whiteAlpha.600" textAlign={{ base: "center", lg: "left" }} maxW="md">
                No card required for the free workspace. AI Usage Credits are optional add-ons when you’re ready.
              </Text>
              <HeroAuxiliaryLinks onExploreFeatures={scrollToFeatures} />
            </Stack>
          </Stack>

          <Box flex="1" w="100%" minW={0} maxW={{ lg: "580px" }}>
            <Box
              as={motion.div}
              initial={reduceMotion ? false : { opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.62, delay: 0.1 }}
            >
              <AssistantShowcase3D />
            </Box>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default Hero;
